/**
 * Exporta un documento Univer (Doc o Sheet) a PDF usando captura de canvas.
 *
 * Motivaci\u00f3n: el flujo anterior basado en `window.print()` + `@media print`
 * ten\u00eda tres problemas:
 *   1. El canvas de Univer s\u00f3lo renderiza el viewport visible. Al
 *      imprimir sin m\u00e1s, salen s\u00f3lo las filas/p\u00e1ginas que
 *      caben en pantalla.
 *   2. La paginaci\u00f3n del navegador rompe el canvas en tiras arbitrarias
 *      que no coinciden con las p\u00e1ginas del documento.
 *   3. El CSS `@media print` se pelea con el posicionamiento fixed del
 *      editor; comportamiento distinto en Chrome/Safari/Firefox.
 *
 * Esta implementaci\u00f3n:
 *   1. Expande temporalmente el wrapper `.ibb-sheet` para que el canvas
 *      de Univer crezca hasta contener todo el documento.
 *   2. Espera al repaint (2 rAF + 300 ms de margen).
 *   3. Captura el canvas entero a una imagen PNG con `toDataURL`.
 *   4. Crea un iframe oculto con HTML autocontenido: una p\u00e1gina por
 *      cada tira de altura "pageHeight" de la imagen original, con
 *      `page-break-after: always`.
 *   5. Llama `print()` sobre el iframe. El di\u00e1logo del navegador
 *      ofrece "Guardar como PDF" y el resultado son p\u00e1ginas A4
 *      limpias con el documento renderizado a resoluci\u00f3n real.
 *   6. Restaura estilos del editor.
 *
 * No usa `@media print` del documento principal \u2192 no hay interferencias
 * con el resto de la app. El iframe gestiona su propia CSS print.
 *
 * API:
 *   await exportUnitToPdf({ mount, sheet, univerAPI, type: 'doc' | 'sheet' });
 */

const A4_ASPECT_H_OVER_W = 297 / 210; // 1.4142
// P\u00e1gina A4 en p\u00edxeles asumiendo el ancho del canvas del documento
// como "ancho de p\u00e1gina". Univer Docs trabaja a 96 DPI por defecto
// (794 x 1123 px = A4). El altura por p\u00e1gina al cortar la imagen
// capturada es canvasWidth * 297/210 (preserva relaci\u00f3n A4 retrato).

/**
 * Estima la altura necesaria para renderizar todo el contenido.
 *
 * Para Docs: usamos `documentStyle.pageSize.height * estimatedPages` donde
 * estimatedPages se deriva de la longitud del dataStream (2000 chars por
 * p\u00e1gina es una heur\u00edstica conservadora) y del n\u00famero de
 * p\u00e1rrafos (40 por p\u00e1gina).
 *
 * Para Sheets: usamos la \u00faltima fila con datos * altura media de fila.
 */
function estimateTargetHeight(type, univerAPI) {
  if (type === 'doc') {
    try {
      const doc = univerAPI.getActiveDocument && univerAPI.getActiveDocument();
      const raw = doc && doc._documentDataModel;
      const snap = raw && raw.snapshot;
      if (!snap) return 1200;
      const ds = snap.documentStyle || {};
      const pageHeight = (ds.pageSize && ds.pageSize.height) || 1123;
      const paragraphs = (snap.body && snap.body.paragraphs) || [];
      const chars = (snap.body && snap.body.dataStream || '').length;
      const byChars = Math.ceil(chars / 2000);
      const byPars = Math.ceil(paragraphs.length / 40);
      const pages = Math.max(1, Math.min(50, Math.max(byChars, byPars)));
      return Math.max(pageHeight, pageHeight * pages + 40);
    } catch (_) { return 1200; }
  }
  // Sheet
  try {
    const wb = univerAPI.getActiveWorkbook && univerAPI.getActiveWorkbook();
    const ws = wb && wb.getActiveSheet && wb.getActiveSheet();
    const dr = ws && ws.getDataRange && ws.getDataRange();
    const lastRow = (dr && dr.getLastRow && dr.getLastRow()) || 20;
    const h = Math.max(1200, lastRow * 22 + 90);
    return Math.min(h, 50 * 1123);
  } catch (_) { return 1200; }
}

/**
 * Recorta verticalmente la imagen capturada en tiras del alto de una
 * p\u00e1gina A4 (preservando la relaci\u00f3n de aspecto 1:1.414 respecto
 * del ancho de la imagen). Devuelve un array de data URLs PNG.
 *
 * Si la \u00faltima tira queda mayormente en blanco (>90% p\u00edxeles
 * blancos), la descartamos \u2192 evita p\u00e1ginas PDF vac\u00edas al final.
 */
function sliceImageIntoPages(sourceCanvas, pageHeight) {
  const srcW = sourceCanvas.width;
  const srcH = sourceCanvas.height;
  const pages = [];
  for (let y = 0; y < srcH; y += pageHeight) {
    const thisH = Math.min(pageHeight, srcH - y);
    if (thisH < 20) break; // tira residual demasiado peque\u00f1a
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = srcW;
    pageCanvas.height = pageHeight; // altura completa: el resto queda blanco
    const ctx = pageCanvas.getContext('2d');
    // Fondo blanco (por si la imagen de origen tiene zonas transparentes).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, srcW, pageHeight);
    ctx.drawImage(sourceCanvas, 0, y, srcW, thisH, 0, 0, srcW, thisH);

    // Heur\u00edstica: descartar p\u00e1ginas mayormente blancas (vac\u00edas).
    if (pages.length > 0 && isMostlyWhite(pageCanvas)) break;
    pages.push(pageCanvas.toDataURL('image/png'));
  }
  return pages;
}

/**
 * Devuelve true si >97% de los p\u00edxeles del canvas son blancos (o
 * casi blancos). Usa un muestreo en malla para no procesar toda la imagen.
 */
function isMostlyWhite(canvas) {
  try {
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    const samples = 200;
    let whiteCount = 0;
    for (let i = 0; i < samples; i++) {
      const x = Math.floor((i / samples) * w);
      const y = Math.floor(((i * 7919) % samples) / samples * h);
      const p = ctx.getImageData(x, y, 1, 1).data;
      if (p[0] > 245 && p[1] > 245 && p[2] > 245) whiteCount++;
    }
    return whiteCount / samples > 0.97;
  } catch (_) { return false; }
}

/**
 * Construye un iframe oculto, mete el HTML con las p\u00e1ginas y dispara
 * `print()` sobre \u00e9l. Devuelve una promesa que resuelve cuando el
 * di\u00e1logo de impresi\u00f3n se cierra (o tras un timeout de seguridad).
 */
function printPagesInIframe(pagesDataURLs, filenameHint) {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '-9999px';
    iframe.style.bottom = '-9999px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.title = filenameHint || 'Iberbox PDF';
    document.body.appendChild(iframe);

    const imgsHtml = pagesDataURLs.map(url =>
      `<div class="p"><img src="${url}" /></div>`
    ).join('');

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(filenameHint || 'Documento')}</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  .p {
    width: 210mm;
    height: 297mm;
    page-break-after: always;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    background: #fff;
  }
  .p:last-child { page-break-after: auto; }
  .p img {
    display: block;
    width: 210mm;
    height: 297mm;
    object-fit: contain;
    object-position: top center;
  }
</style>
</head>
<body>
${imgsHtml}
</body>
</html>`;

    const doc = iframe.contentDocument;
    doc.open();
    doc.write(html);
    doc.close();

    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch (_) { /* noop */ }
      resolve();
    };

    // Esperar a que las im\u00e1genes se carguen antes de imprimir.
    const imgs = [...doc.images];
    let loaded = 0;
    const onAllLoaded = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (_) { /* noop */ }
      // Chrome dispara `afterprint` en el iframe; Safari no.
      const cw = iframe.contentWindow;
      let done = false;
      const finish = () => { if (done) return; done = true; cleanup(); };
      try { cw.addEventListener('afterprint', finish, { once: true }); } catch (_) { /* noop */ }
      // Fallback: 2 s despu\u00e9s eliminamos el iframe aunque el usuario
      // siga en el di\u00e1logo (el di\u00e1logo ya tiene su copia del
      // HTML rendido).
      setTimeout(finish, 2500);
    };
    if (imgs.length === 0) {
      setTimeout(onAllLoaded, 50);
    } else {
      imgs.forEach(img => {
        if (img.complete) {
          if (++loaded === imgs.length) onAllLoaded();
        } else {
          img.addEventListener('load', () => {
            if (++loaded === imgs.length) onAllLoaded();
          });
          img.addEventListener('error', () => {
            if (++loaded === imgs.length) onAllLoaded();
          });
        }
      });
    }
  });
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;'
  }[c]));
}

/**
 * Espera a que el pr\u00f3ximo paint se complete (2 rAFs + delay extra).
 */
function waitForRepaint(extraMs = 300) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, extraMs);
      });
    });
  });
}

/**
 * Fallback: restaurar estilos y usar `window.print()` del main doc con la
 * clase `ibb-sheet-printing` (que activa las reglas `@media print` de
 * `ibb-editor.css` que ocultan UI del cliente). Peor paginaci\u00f3n pero
 * funciona siempre como last resort.
 */
function fallbackPrintMain(sheet, mount, prevSheet, prevMount) {
  // Restaurar primero.
  if (sheet) sheet.setAttribute('style', prevSheet);
  if (mount) mount.setAttribute('style', prevMount);
  window.dispatchEvent(new Event('resize'));

  document.body.classList.add('ibb-sheet-printing');
  window.dispatchEvent(new Event('resize'));
  const cleanup = () => {
    document.body.classList.remove('ibb-sheet-printing');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  setTimeout(() => {
    try { window.print(); } finally {
      setTimeout(cleanup, 800);
    }
  }, 200);
}

/**
 * Flujo completo de exportaci\u00f3n a PDF.
 *
 * @param {Object} opts
 * @param {HTMLElement} opts.mount      El `.ibb-sheet__mount` del editor.
 * @param {HTMLElement} opts.sheet      El `.ibb-sheet` root (para romper fixed).
 * @param {Object} opts.univerAPI       Instancia de FUniver API.
 * @param {'doc'|'sheet'} opts.type     Tipo de documento.
 * @param {string} [opts.filename]      Nombre sugerido para el PDF.
 */
export async function exportUnitToPdf({ mount, sheet, univerAPI, type, filename }) {
  if (!mount || !sheet || !univerAPI) {
    window.print();
    return;
  }

  const prevSheet = sheet.getAttribute('style') || '';
  const prevMount = mount.getAttribute('style') || '';

  // Canvas antes de expandir (para comparar despu\u00e9s).
  const initialCanvas = mount.querySelector('canvas.univer-render-canvas');
  const initialCanvasH = initialCanvas ? initialCanvas.height : 0;

  // 1. Calcular altura target.
  const targetHeight = estimateTargetHeight(type, univerAPI);

  // 2. Romper fixed y expandir.
  sheet.style.position = 'relative';
  sheet.style.top = 'auto';
  sheet.style.bottom = 'auto';
  sheet.style.height = (targetHeight + 80) + 'px';
  mount.style.height = targetHeight + 'px';
  window.dispatchEvent(new Event('resize'));

  // 3. Esperar repaint.
  await waitForRepaint(400);

  // 4. Verificar que el canvas ha crecido. Si Univer no propag\u00f3 el
  //    resize (su ResizeObserver interno a veces se queda atr\u00e1s),
  //    capturar un canvas viewport-sized y romperlo en p\u00e1ginas A4
  //    no funciona: s\u00f3lo tendr\u00edamos la p\u00e1gina 1. En ese
  //    caso usamos el fallback de `@media print` + `window.print()`.
  const canvas = mount.querySelector('canvas.univer-render-canvas');
  if (!canvas) {
    fallbackPrintMain(sheet, mount, prevSheet, prevMount);
    return;
  }

  const canvasGrew = canvas.height > initialCanvasH * 1.5 ||
                     canvas.height > 1500; // umbral heur\u00edstico

  if (!canvasGrew && targetHeight > 1500) {
    // El canvas no creci\u00f3 pero el documento es grande. Fallback.
    fallbackPrintMain(sheet, mount, prevSheet, prevMount);
    return;
  }

  // 5. Capturar y partir en p\u00e1ginas A4.
  let pages;
  try {
    const pageHeight = Math.round(canvas.width * A4_ASPECT_H_OVER_W);
    pages = sliceImageIntoPages(canvas, pageHeight);
  } catch (e) {
    fallbackPrintMain(sheet, mount, prevSheet, prevMount);
    return;
  }

  // 6. Restaurar estilos del editor ANTES de abrir el di\u00e1logo.
  sheet.setAttribute('style', prevSheet);
  mount.setAttribute('style', prevMount);
  window.dispatchEvent(new Event('resize'));

  if (!pages.length) {
    fallbackPrintMain(sheet, mount, prevSheet, prevMount);
    return;
  }

  // 7. Imprimir en iframe (aislado de la app).
  await printPagesInIframe(pages, filename);
}
